/*
 * The medicines round.
 *
 * Round tabs, then the residents with how far through they are, then one resident's doses.
 * Recording asks for what the safety rules need and nothing else: a reason when a dose was not
 * given, a witness for a controlled drug, a reason when a dose is given before it is due.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, record } from './api.ts';
import type { Dose, ReportedChange, Round, RoundResident, User, Witness } from './api.ts';
import { ReportedWarning } from './reported.tsx';
import { BodyMap } from './bodymap.tsx';
import type { SiteUse } from './bodymap.tsx';
import { ChartScreen } from './chart.tsx';

type SiteOption = { id: string; label: string; side: 'front' | 'back' };

type InrCurrent = {
  value: number;
  testedOn: string;
  doseText: string;
  nextTestOn: string | null;
  source: string;
  overdue: boolean;
  daysOverdue: number;
};

/* Kept in step with PEG_CHECKS on the server, which is what actually enforces them. */
const PEG_CHECKS = [
  { id: 'position', label: 'The resident is sitting up at 45 degrees or more' },
  { id: 'feedPaused', label: 'The feed is paused' },
  { id: 'placement', label: 'The tube position has been checked' },
  { id: 'flushedBefore', label: 'Flushed before' },
  { id: 'flushedAfter', label: 'Flushed after, and the feed restarted' },
];
import { useOnline } from './connection.tsx';
import { onOutboxChange, recall, remember, waiting as waitingToSend } from './offline.ts';
import { CycleBanner } from './cycle.tsx';
import { FluidSection } from './fluids.tsx';
import { HomelySection } from './homely.tsx';
import { Overlay } from './overlay.tsx';
import { PrnSection } from './prn-meds.tsx';
import { SlotCard } from './slot.tsx';

export function RoundScreen({ homeId, user }: { homeId: string; user: User }) {
  const [rounds, setRounds] = useState<Round[]>([]);
  const [roundId, setRoundId] = useState<string>('all');
  const [chosen, setChosen] = useState(false);
  const [residents, setResidents] = useState<RoundResident[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<{ rounds: Round[] }>(`/api/homes/${homeId}/rounds`)
      .then((data) => setRounds(data.rounds))
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not load the rounds.'));
  }, [homeId]);

  // Start on the round that is happening now.
  useEffect(() => {
    if (chosen || !rounds.length) return;
    const now = clock(new Date());
    const current = rounds.find((r) => now >= r.startsAt && now <= r.endsAt);
    setRoundId(current ? current.id : 'all');
    setChosen(true);
  }, [rounds, chosen]);

  const [asOf, setAsOf] = useState<number | null>(null);

  const load = useCallback(async () => {
    const key = `round|${homeId}|${roundId}`;
    try {
      const query = roundId === 'all' ? '' : `?roundId=${roundId}`;
      const data = await api.get<{ residents: RoundResident[] }>(`/api/homes/${homeId}/round${query}`);
      setResidents(data.residents);
      setAsOf(null);
      setError('');
      void remember(key, data.residents);
    } catch (err) {
      // No signal: show the list as it was when it was last fetched, and say so.
      if (err instanceof ApiError && err.status === 0) {
        const saved = await recall<RoundResident[]>(key).catch(() => null);
        if (saved) {
          setResidents(saved.value);
          setAsOf(saved.at);
          setError('');
          return;
        }
      }
      setError(err instanceof ApiError ? err.message : 'Could not load the round.');
    }
  }, [homeId, roundId]);

  useEffect(() => {
    if (!openId) void load();
  }, [load, openId]);

  if (openId) {
    return (
      <ResidentDoses
        residentId={openId}
        homeId={homeId}
        user={user}
        onBack={() => setOpenId(null)}
      />
    );
  }

  const waiting = (residents ?? []).filter((r) => r.total > r.done);

  return (
    <section>
      <div className="head">
        <h1>Medicines round</h1>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
      </div>

      <nav className="tabs">
        <button type="button" className={roundId === 'all' ? 'on' : ''} onClick={() => setRoundId('all')}>
          All today
        </button>
        {rounds.map((r) => (
          <button type="button" key={r.id} className={roundId === r.id ? 'on' : ''} onClick={() => setRoundId(r.id)}>
            {r.name} <span className="muted small">{r.dueAt}</span>
          </button>
        ))}
      </nav>

      <CycleBanner homeId={homeId} />

      {error ? <p className="error" role="alert">{error}</p> : null}
      {asOf ? (
        <p className="notice warn">
          <strong>No connection.</strong> This is the list as it was at {formatTime(new Date(asOf).toISOString())}.
          Anything changed since then will not show until the signal is back.
        </p>
      ) : null}
      {residents === null ? <p className="muted">Loading…</p> : null}
      {residents && !waiting.length ? (
        <p className="notice">Everything in this round has been recorded.</p>
      ) : null}

      <ul className="list">
        {(residents ?? []).map((r) => (
          <li key={r.id}>
            <button type="button" className="row" onClick={() => setOpenId(r.id)} disabled={r.total === 0}>
              <span className="row-main">
                {r.name}
                {r.room ? <span className="muted small"> · Room {r.room}</span> : null}
              </span>
              <span className="chips">
                {r.overdue ? <span className="chip warn">{r.overdue} overdue</span> : null}
                {r.reported ? <span className="chip warn">Change reported</span> : null}
                {r.total === 0 ? (
                  <span className="chip">Nothing due</span>
                ) : (
                  <span className={`chip ${r.done === r.total ? 'done' : ''}`}>{r.done} of {r.total} done</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

type DayResident = { id: string; name: string; room: string; dateOfBirth: string };

function ResidentDoses({
  residentId,
  homeId,
  user,
  onBack,
}: {
  residentId: string;
  homeId: string;
  user: User;
  onBack: () => void;
}) {
  const [resident, setResident] = useState<DayResident | null>(null);
  const [doses, setDoses] = useState<Dose[] | null>(null);
  const [codes, setCodes] = useState<Record<string, string>>({});
  // Reported changes that are not about one of today's doses — a new medicine, say.
  const [reports, setReports] = useState<ReportedChange[]>([]);
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [error, setError] = useState('');
  const [recording, setRecording] = useState<Dose | null>(null);
  const [correcting, setCorrecting] = useState<Dose | null>(null);
  const [showChart, setShowChart] = useState(false);
  // Doses recorded on this tablet that the server has not been told about yet.
  const [unsent, setUnsent] = useState<Set<string>>(new Set());

  const [asOf, setAsOf] = useState<number | null>(null);

  const load = useCallback(async () => {
    const key = `doses|${residentId}`;
    type Answer = { resident: DayResident; doses: Dose[]; codes: Record<string, string>; reports?: ReportedChange[] };
    try {
      const data = await api.get<Answer>(`/api/residents/${residentId}/doses`);
      setResident(data.resident);
      setDoses(data.doses);
      setCodes(data.codes);
      setReports(data.reports ?? []);
      setAsOf(null);
      setError('');
      void remember(key, data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 0) {
        const saved = await recall<Answer>(key).catch(() => null);
        if (saved) {
          setResident(saved.value.resident);
          setDoses(saved.value.doses);
          setCodes(saved.value.codes);
          // Most needed with no signal: a carer offline still sees what the home reported.
          setReports(saved.value.reports ?? []);
          setAsOf(saved.at);
          setError('');
          return;
        }
      }
      setError(err instanceof ApiError ? err.message : 'Could not load the medicines.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses))
      .catch(() => setWitnesses([]));
  }, [load, homeId]);

  /*
   * Watch the outbox. When something in it is sent, the screen is loaded again so the dose shows
   * with the server's record — and so a "no connection" message from a moment ago goes away.
   */
  const lookInOutbox = useCallback(() => {
    void waitingToSend().then((map) => {
      setUnsent((before) => {
        const now = new Set(map.keys());
        if (before.size && now.size < before.size) void load();
        return now;
      });
    });
  }, [load]);

  useEffect(() => {
    lookInOutbox();
    return onOutboxChange(lookInOutbox);
  }, [lookInOutbox]);

  const isUnsent = useCallback(
    (dose: Dose) => unsent.has(`${dose.medicineId}|${dose.dueOn}|${dose.dueAt}`),
    [unsent],
  );

  const done = useMemo(() => (doses ?? []).filter((d) => d.record || isUnsent(d)), [doses, isUnsent]);

  /*
   * Anything in a tray is recorded as a slot, because that is how it is given: one pod, popped
   * once. Everything else is a card of its own.
   */
  const { slots, todo } = useMemo(() => {
    // A dose sitting in the outbox has been given. It must not appear as still to do.
    const waiting = (doses ?? []).filter((d) => !d.record && !isUnsent(d));
    const byTime = new Map<string, Dose[]>();
    const singles: Dose[] = [];
    for (const dose of waiting) {
      if (dose.trayType === 'none') {
        singles.push(dose);
        continue;
      }
      const key = `${dose.dueAt}|${dose.trayType}`;
      byTime.set(key, [...(byTime.get(key) ?? []), dose]);
    }
    // A tray with one thing in it is just a dose; no need for the ceremony of a slot.
    const groups: Dose[][] = [];
    for (const group of byTime.values()) {
      if (group.length > 1) groups.push(group);
      else singles.push(...group);
    }
    groups.sort((a, b) => (a[0]?.dueAt ?? '').localeCompare(b[0]?.dueAt ?? ''));
    return { slots: groups, todo: singles };
  }, [doses, isUnsent]);

  if (showChart) return <ChartScreen residentId={residentId} onBack={() => setShowChart(false)} />;

  return (
    <section>
      <div className="head">
        <div>
          <h1>{resident?.name ?? 'Resident'}</h1>
          <p className="muted small">
            {resident?.room ? `Room ${resident.room}` : ''}
            {resident?.dateOfBirth ? ` · born ${formatDate(resident.dateOfBirth)}` : ''}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="quiet" onClick={() => setShowChart(true)}>MAR chart</button>
          <button type="button" className="quiet" onClick={onBack}>Back to the round</button>
        </div>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {asOf ? (
        <p className="notice warn">
          <strong>No connection.</strong> This is the list as it was at {formatTime(new Date(asOf).toISOString())}.
          Anything changed since then will not show until the signal is back.
        </p>
      ) : null}
      {doses === null ? <p className="muted">Loading…</p> : null}

      {reports.map((report) => (
        <ReportedWarning key={report.id} report={report} inBlister={false} />
      ))}

      {slots.length || todo.length ? <h2>To give</h2> : null}

      <ul className="list">
        {slots.map((group) => (
          <li key={group[0]?.key}>
            <SlotCard
              residentId={residentId}
              doses={group}
              codes={codes}
              witnesses={witnesses}
              onRecorded={() => void load()}
            />
          </li>
        ))}
        {todo.map((dose) => (
          <li key={dose.key}>
            <DoseCard dose={dose} onRecord={() => setRecording(dose)} />
          </li>
        ))}
      </ul>

      {done.length ? <h2>Recorded</h2> : null}
      <ul className="list">
        {done.map((dose) => (
          <li key={dose.key}>
            <DoseCard
              dose={dose}
              unsent={isUnsent(dose)}
              onCorrect={dose.record ? () => setCorrecting(dose) : undefined}
            />
          </li>
        ))}
      </ul>

      {doses && !doses.length ? <p className="muted">Nothing is due at a set time for this resident today.</p> : null}

      <PrnSection residentId={residentId} homeId={homeId} />

      <HomelySection residentId={residentId} />

      <FluidSection
        residentId={residentId}
        canPlan={user.roles.includes('nurse') || user.roles.includes('check') || user.roles.includes('manage')}
      />

      {recording ? (
        <RecordDialog
          dose={recording}
          homeId={homeId}
          residentId={residentId}
          codes={codes}
          user={user}
          onClose={() => setRecording(null)}
          onDone={() => {
            setRecording(null);
            void load();
          }}
        />
      ) : null}

      {correcting ? (
        <CorrectDialog
          dose={correcting}
          codes={codes}
          onClose={() => setCorrecting(null)}
          onDone={() => {
            setCorrecting(null);
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

function DoseCard({
  dose,
  onRecord,
  onCorrect,
  unsent,
}: {
  dose: Dose;
  onRecord?: () => void;
  onCorrect?: () => void;
  /* Recorded on this tablet with no signal, and not yet sent. */
  unsent?: boolean;
}) {
  const record = dose.record;
  return (
    <div className={`row static column dose ${dose.status}`}>
      <div className="row-line">
        <span className="row-main">{dose.name}</span>
        <span className="time">{dose.dueAt}</span>
      </div>
      {dose.reported ? <ReportedWarning report={dose.reported} /> : null}
      <p className="dose-amount">{dose.doseText}</p>
      <p className="muted small">
        {dose.directions}
        {dose.slotNote ? ` · ${dose.slotNote}` : ''}
      </p>

      <div className="chips">
        {dose.status === 'overdue' ? <span className="chip warn">Overdue</span> : null}
        {dose.late ? <span className="chip warn">Late, time critical</span> : null}
        {dose.timeCritical ? <span className="chip">Time critical</span> : null}
        {dose.controlledDrug ? <span className="chip">Controlled drug</span> : null}
        {dose.nurseOnly ? <span className="chip">Nurses only</span> : null}
        {dose.trayType !== 'none' ? <span className="chip">{dose.trayType === 'separate' ? 'Separate tray' : 'In the tray'}{dose.trayLooks ? `: ${dose.trayLooks}` : ''}</span> : null}
        {dose.trayType === 'none' && dose.notTrayReason ? <span className="chip">{dose.notTrayReason}</span> : null}
      </div>

      {record ? (
        <p className="muted small">
          {record.outcome === 'given' ? 'Given' : `Not given: ${record.codeLabel}`} by {record.recordedBy} at{' '}
          {formatTime(record.recordedAt)}
          {record.witness ? `, witnessed by ${record.witness}` : ''}
          {record.note ? ` · ${record.note}` : ''}
          {record.corrected ? ` · corrected: ${record.reason}` : ''}
        </p>
      ) : null}

      {unsent && !record ? (
        <p className="muted small">
          <strong>Recorded on this tablet.</strong> It will be sent when the signal comes back. Do not give
          it again.
        </p>
      ) : null}

      <div className="row-actions">
        {onRecord ? <button type="button" onClick={onRecord}>Record</button> : null}
        {onCorrect && !unsent ? (
          <button type="button" className="quiet" onClick={onCorrect}>Correct</button>
        ) : null}
      </div>
    </div>
  );
}

function RecordDialog({
  dose,
  homeId,
  residentId,
  codes,
  user,
  onClose,
  onDone,
}: {
  dose: Dose;
  homeId: string;
  residentId: string;
  codes: Record<string, string>;
  user: User;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'given' | 'not_given'>('given');
  const [code, setCode] = useState('R');
  const [note, setNote] = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [earlyReason, setEarlyReason] = useState('');
  const [askEarly, setAskEarly] = useState(dose.status === 'upcoming');
  const online = useOnline();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [site, setSite] = useState('');
  const [sites, setSites] = useState<{ sites: SiteOption[]; recent: SiteUse[]; restDays: number } | null>(null);
  const [glucose, setGlucose] = useState('');
  const [pegDone, setPegDone] = useState<Record<string, boolean>>({});
  const [inr, setInr] = useState<InrCurrent | null>(null);
  const [inrReason, setInrReason] = useState('');
  const needsSite = dose.topical || dose.patch || dose.insulin;

  // Warfarin has no dose on its label: it comes from the latest INR, so fetch it first.
  useEffect(() => {
    if (!dose.warfarin) return;
    api
      .get<{ current: InrCurrent | null }>(`/api/residents/${residentId}/inr`)
      .then((data) => setInr(data.current))
      .catch(() => setInr(null));
  }, [dose.warfarin, residentId]);

  // Creams and patches are recorded on the body map, so fetch the sites and what is still resting.
  useEffect(() => {
    if (!needsSite) return;
    api
      .get<{ sites: SiteOption[]; recent: SiteUse[]; restDays: number }>(`/api/residents/${residentId}/sites`)
      .then(setSites)
      .catch(() => setSites(null));
  }, [needsSite, residentId]);

  useEffect(() => {
    if (!dose.controlledDrug) return;
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses))
      .catch(() => setWitnesses([]));
  }, [dose.controlledDrug, homeId]);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        const clientId = crypto.randomUUID();
        await record(
          '/api/doses',
          {
            clientId,
            medicineId: dose.medicineId,
            dueOn: dose.dueOn,
            dueAt: dose.dueAt,
            outcome,
            code: outcome === 'not_given' ? code : undefined,
            note,
            witnessId: witnessId || undefined,
            earlyReason: earlyReason || undefined,
            inrOverdueReason: inrReason || undefined,
            detail: {
              ...(site ? { site } : {}),
              ...(glucose ? { glucose: Number(glucose) } : {}),
              ...(dose.peg ? { peg: pegDone } : {}),
            },
          },
          `${dose.name} at ${dose.dueAt}`,
          clientId,
        );
        onDone();
      } catch (err) {
        if (err instanceof ApiError && err.status === 409 && /not due until/.test(err.message)) {
          setAskEarly(true);
        }
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [dose, outcome, code, note, witnessId, earlyReason, site, glucose, pegDone, inrReason, onDone],
  );

  /*
   * A controlled drug is the one thing that cannot be recorded without a connection. The running
   * balance in the register lives on the server, and two tablets recording against it from
   * different corridors would put the register out — which is the one number that has to match the
   * cupboard exactly. Better to wait for the signal, or write it in the paper register.
   */
  if (dose.controlledDrug && !online) {
    return (
      <Overlay onClose={onClose}>
        <div className="card wide">
          <h2>{dose.name}</h2>
          <p className="muted">{dose.doseText} · due {dose.dueAt}</p>
          <p className="notice warn">
            <strong>This tablet has no connection, and this is a controlled drug.</strong> The register balance
            is kept on the server, so it cannot be recorded here until the signal is back.
          </p>
          <p className="muted">
            Give the dose with a second person as usual and record it in the paper controlled drugs register,
            then enter it here when the tablet is back online. Everything else on the round can be recorded as
            normal in the meantime.
          </p>
          <div className="row-actions">
            <button type="button" onClick={onClose}>Back to the round</button>
          </div>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{dose.name}</h2>
        <p className="muted">
          {dose.doseText} · due {dose.dueAt} · {user.name}
        </p>
        {!online ? (
          <p className="notice">
            No connection. This will be saved on the tablet and sent when the signal comes back.
          </p>
        ) : null}
        {dose.trayType !== 'none' && dose.trayLooks ? (
          <p className="notice">In the tray: {dose.trayLooks}. Check the blister is marked {dose.dueAt}.</p>
        ) : null}

        <fieldset>
          <legend>What happened</legend>
          <label className="check">
            <input type="radio" name="outcome" checked={outcome === 'given'} onChange={() => setOutcome('given')} />
            <span><strong>Given</strong></span>
          </label>
          <label className="check">
            <input type="radio" name="outcome" checked={outcome === 'not_given'} onChange={() => setOutcome('not_given')} />
            <span><strong>Not given</strong></span>
          </label>
        </fieldset>

        {dose.insulin && outcome === 'given' ? (
          <>
            <label htmlFor="glucose">Blood glucose before the dose (mmol/L)</label>
            <input id="glucose" type="number" inputMode="decimal" step="0.1" min="0.5" max="40" required
              value={glucose} onChange={(e) => setGlucose(e.target.value)} />
            {glucose && Number(glucose) < 4 ? (
              <p className="notice warn">
                {glucose} is a hypo. Do not give the insulin. Treat the hypo now and tell the nurse.
              </p>
            ) : null}
          </>
        ) : null}

        {dose.warfarin && outcome === 'given' ? (
          inr ? (
            <>
              <p className="notice">
                <strong>Give {inr.doseText}</strong> — from the INR of {inr.value} on {formatDate(inr.testedOn)},{' '}
                {inr.source}.{inr.nextTestOn ? ` Next test ${formatDate(inr.nextTestOn)}.` : ''}
              </p>
              {inr.overdue ? (
                <>
                  <p className="notice warn">
                    The retest was due {formatDate(inr.nextTestOn ?? '')}, {inr.daysOverdue} days ago. Ring the clinic
                    before giving this.
                  </p>
                  <label htmlFor="inr-reason">What did the clinic say?</label>
                  <input id="inr-reason" value={inrReason} onChange={(e) => setInrReason(e.target.value)}
                    placeholder="Same dose, retest booked for Friday" />
                </>
              ) : null}
            </>
          ) : (
            <p className="notice warn">
              There is no INR result on the system, so there is no dose to give. Ring the anticoagulant clinic.
            </p>
          )
        ) : null}

        {dose.peg && outcome === 'given' ? (
          <fieldset>
            <legend>Before it goes down the tube</legend>
            {PEG_CHECKS.map((check) => (
              <label className="check" key={check.id}>
                <input type="checkbox" checked={!!pegDone[check.id]}
                  onChange={(e) => setPegDone({ ...pegDone, [check.id]: e.target.checked })} />
                <span>{check.label}</span>
              </label>
            ))}
          </fieldset>
        ) : null}

        {needsSite && outcome === 'given' ? (
          sites ? (
            <fieldset>
              <legend>
                {dose.patch
                  ? 'Where did the patch go?'
                  : dose.insulin
                    ? 'Where was the injection given?'
                    : 'Where was it put on?'}
              </legend>
              <BodyMap
                sites={sites.sites}
                value={site}
                onChange={setSite}
                recent={sites.recent}
                restDays={sites.restDays}
              />
              {dose.patch ? (
                <p className="muted small">
                  Patches need {sites.restDays} days before the same skin is used again. Remove the old one first.
                </p>
              ) : null}
            </fieldset>
          ) : (
            <p className="muted">Loading the body map…</p>
          )
        ) : null}

        {outcome === 'not_given' ? (
          <>
            <label htmlFor="code">Reason</label>
            <select id="code" value={code} onChange={(e) => setCode(e.target.value)}>
              {Object.entries(codes)
                .filter(([letter]) => letter !== 'V')
                .map(([letter, label]) => (
                  <option key={letter} value={letter}>{letter} — {label}</option>
                ))}
            </select>
          </>
        ) : null}

        {dose.controlledDrug && outcome === 'given' ? (
          <>
            <label htmlFor="witness">Witness</label>
            <select id="witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
              <option value="">Choose who watched this</option>
              {witnesses.map((w) => (
                <option key={w.id} value={w.id}>{w.name} — {w.jobTitle}</option>
              ))}
            </select>
            <p className="muted small">A controlled drug needs a second trained person.</p>
          </>
        ) : null}

        {askEarly ? (
          <>
            <label htmlFor="early">This is before it is due. Why?</label>
            <input id="early" value={earlyReason} onChange={(e) => setEarlyReason(e.target.value)}
              placeholder="Going out to an appointment" />
          </>
        ) : null}

        <label htmlFor="note">Note {outcome === 'not_given' && code === 'O' ? '' : '(optional)'}</label>
        <input id="note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save this record'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function CorrectDialog({
  dose,
  codes,
  onClose,
  onDone,
}: {
  dose: Dose;
  codes: Record<string, string>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<'given' | 'not_given'>(dose.record?.outcome ?? 'given');
  const [code, setCode] = useState(dose.record?.code ?? 'R');
  const [note, setNote] = useState(dose.record?.note ?? '');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/doses/${dose.record?.id}/correct`, {
          outcome,
          code: outcome === 'not_given' ? code : undefined,
          note,
          reason,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [dose, outcome, code, note, reason, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Correct this record</h2>
        <p className="muted">
          {dose.name} · due {dose.dueAt}. The original stays in the record, with your correction underneath.
        </p>

        <fieldset>
          <legend>It should say</legend>
          <label className="check">
            <input type="radio" name="fix" checked={outcome === 'given'} onChange={() => setOutcome('given')} />
            <span><strong>Given</strong></span>
          </label>
          <label className="check">
            <input type="radio" name="fix" checked={outcome === 'not_given'} onChange={() => setOutcome('not_given')} />
            <span><strong>Not given</strong></span>
          </label>
        </fieldset>

        {outcome === 'not_given' ? (
          <>
            <label htmlFor="fix-code">Reason</label>
            <select id="fix-code" value={code} onChange={(e) => setCode(e.target.value)}>
              {Object.entries(codes)
                .filter(([letter]) => letter !== 'V')
                .map(([letter, label]) => (
                  <option key={letter} value={letter}>{letter} — {label}</option>
                ))}
            </select>
          </>
        ) : null}

        <label htmlFor="fix-note">Note</label>
        <input id="fix-note" value={note} onChange={(e) => setNote(e.target.value)} />

        <label htmlFor="fix-reason">Why is it being corrected?</label>
        <input id="fix-reason" required minLength={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Recorded against the wrong resident" />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save the correction'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function clock(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

function formatTime(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
